// ft3d-hud/project-roll.ts — turn one authorized roll into an FtLogEntry.
//
// Input is the `ft-roll-authorized` detail republished by handleRollInsert() AFTER its
// stale-encounter drop, roll_i dedupe and grouping decision. Viewer privacy uses the same
// predicate as the full log, independently of any permission stamped onto the row.

import type { FtLogAdvDisSource, FtLogAdvDisTrace, FtLogContest, FtLogEntry, FtLogSegment } from './types';
import { rollVisibility } from '../janus-engine/rolls/visibility';
import {
    actorFrom,
    actorFromCell,
    eventIconFor,
    renderedEventIconFor,
    targetBlockIn,
    targetFrom,
    timesFrom,
    timestampSpans,
} from './row-anatomy';

/** Total characters a projected entry may carry before it is elided. */
const SEGMENT_TEXT_CAP = 190;

/**
 * Nodes whose text must never reach the log line: screen-reader-only prose, the collapsed
 * gate-suppressed dice breakdown (hidden in the real row, so showing it here would leak damage the
 * log deliberately folded away), Bootstrap's hiding utilities, and anything the renderer inlined as
 * hidden. The row arrives detached, so computed style is unavailable — this list has to name every
 * way the log renderer expresses "not shown".
 */
const DROP_SELECTOR = [
    'script',
    'style',
    '.visually-hidden',
    '.visually-hidden-focusable',
    '.gate-suppressed-dice-breakdown',
    '.d-none',
    '.collapse:not(.show)',
    '[hidden]',
    '[aria-hidden="true"]',
].join(',');

function isDropped(el: Element): boolean {
    if (el.matches(DROP_SELECTOR)) return true;
    const inline = el.getAttribute('style') || '';
    return /display\s*:\s*none/i.test(inline) || /visibility\s*:\s*hidden/i.test(inline);
}

/**
 * The text of an element that has CLAIMED a segment kind. Its descendants are no longer walked, so
 * the drop rules have to be applied here too — otherwise a hidden child's text would ride along
 * inside a claimed badge or reference.
 */
function claimedText(el: Element): string {
    if (el.querySelector(DROP_SELECTOR) === null && !/visibility\s*:\s*hidden|display\s*:\s*none/i.test(el.innerHTML)) {
        return el.textContent || '';
    }
    const clone = el.cloneNode(true) as Element;
    clone.querySelectorAll('*').forEach((child) => {
        if (isDropped(child)) child.remove();
    });
    return clone.textContent || '';
}

/** Reads the badge's inline background-color, which the renderer stamped from DAMAGETYPE_CATALOG. */
function inlineBackgroundHex(el: Element): string | null {
    const inline = el.getAttribute('style') || '';
    const match = /background-color\s*:\s*([^;]+)/i.exec(inline);
    if (!match || !match[1]) return null;
    const value = match[1].trim();
    // Only the shapes DAMAGETYPE_CATALOG actually produces. Anything else is dropped rather than
    // forwarded into a custom property, so the log can never inherit an unexpected CSS token.
    return /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/deg]+\))$/i.test(value) ? value : null;
}

function tooltipFor(el: Element): Pick<FtLogSegment, 'tooltip' | 'tooltipPlacement'> {
    const tooltip = (
        el.getAttribute('data-bs-original-title')
        || el.getAttribute('title')
        || ''
    ).trim();
    if (!tooltip) return {};
    const tooltipPlacement = (el.getAttribute('data-bs-placement') || '').trim() || undefined;
    return { tooltip, tooltipPlacement };
}

function segmentKindFor(el: Element): { kind: FtLogSegment['kind']; itemId?: string; colorHex?: string; tooltip?: string; tooltipPlacement?: string; isKept?: boolean } | null {
    const tooltip = tooltipFor(el);
    if (el.getAttribute('data-ft-log-action') === 'open-item-info') {
        const itemId = el.getAttribute('data-ft-item-id') || '';
        if (itemId !== '') return { kind: 'ref', itemId, ...tooltip };
    }
    if (el.classList.contains('badge')) {
        // Current rows stamp semantic data; stored rows predating that hook retain the established
        // "D20 ADV 1" / "D20 DIS 2" tooltip and Bootstrap kept-die colour as a compatibility lane.
        const stampedTone = (el.getAttribute('data-ft-adv-dis-kind') || '').toLowerCase();
        const tooltipTone = /\b(ADV|DIS)\s+\d+(?:\s*·|$)/i.exec(tooltip.tooltip || '')?.[1]?.toLowerCase() || '';
        const tone = stampedTone === 'adv' || stampedTone === 'dis' ? stampedTone : tooltipTone;
        if (tone === 'adv' || tone === 'dis') {
            const stampedKept = el.getAttribute('data-ft-adv-dis-kept');
            const isKept = stampedKept === '1'
                || (stampedKept === null && el.classList.contains(tone === 'adv' ? 'bg-success' : 'bg-danger'));
            return { kind: tone === 'adv' ? 'adv-die' : 'dis-die', isKept, ...tooltip };
        }
        const colorHex = inlineBackgroundHex(el);
        const essence = /^(Body|Mind|Soul) Essence\b/i.exec(tooltip.tooltip || '');
        if (essence?.[1]) {
            // Match the legacy log contract: the number stays terse, its established red/blue/purple
            // color communicates Body/Mind/Soul, and the full essence name remains in the tooltip.
            return { kind: 'essence', colorHex: colorHex || undefined, ...tooltip };
        }
        if (colorHex) return { kind: 'damage', colorHex, ...tooltip };
        return { kind: 'effect', ...tooltip };
    }
    const tag = el.tagName.toLowerCase();
    if (tag === 'h2' || el.classList.contains('h2')) return { kind: 'result', ...tooltip };
    return null;
}

function pushSegment(out: FtLogSegment[], seg: FtLogSegment): void {
    const text = seg.text.replace(/\s+/g, ' ').trim();
    if (text === '') return;
    const prev = out[out.length - 1];
    // Merge runs of prose so a row built from a dozen nested <span>s reads as one sentence.
    if (prev && prev.kind === 'text' && seg.kind === 'text') {
        prev.text = `${prev.text} ${text}`;
        return;
    }
    out.push({ ...seg, text });
}

/**
 * Depth-first walk that stops descending as soon as a node claims a non-text kind, so a clickable
 * name or a coloured die badge arrives as ONE segment rather than as its inner spans.
 */
function collectSegments(node: Node, out: FtLogSegment[]): void {
    if (node.nodeType === Node.TEXT_NODE) {
        pushSegment(out, { kind: 'text', text: node.textContent || '' });
        return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;
    if (isDropped(el)) return;

    const claimed = segmentKindFor(el);
    if (claimed) {
        pushSegment(out, { ...claimed, text: claimedText(el) });
        return;
    }
    node.childNodes.forEach((child) => collectSegments(child, out));
}

function isAdvDisDie(seg: FtLogSegment | undefined): boolean {
    return seg?.kind === 'adv-die' || seg?.kind === 'dis-die';
}

/**
 * The full log uses parentheses around an ADV/DIS pool because its generic Bootstrap badges have
 * no shared visual container. The compact HUD carries a dedicated ADV/DIS event glyph plus selected
 * and discarded die treatments, so those parentheses become noise. Remove only punctuation that
 * directly hugs a recognized semantic pool; ordinary prose and calculations remain untouched.
 */
function compactAdvDisPunctuation(segments: FtLogSegment[]): FtLogSegment[] {
    const out: FtLogSegment[] = [];
    segments.forEach((seg, index) => {
        if (seg.kind !== 'text') {
            out.push(seg);
            return;
        }
        let text = seg.text;
        if (isAdvDisDie(segments[index + 1])) text = text.replace(/\s*\($/, '');
        if (isAdvDisDie(segments[index - 1])) text = text.replace(/^\)\s*/, '');
        if (text.trim() !== '') out.push({ ...seg, text });
    });
    return out;
}

function segmentPlainText(seg: FtLogSegment): string {
    if (seg.isKept && seg.kind === 'adv-die') return `ADV ${seg.text}`;
    if (seg.isKept && seg.kind === 'dis-die') return `DIS ${seg.text}`;
    return seg.text;
}

function segmentsPlainText(segments: FtLogSegment[]): string {
    return segments.map(segmentPlainText).join(' ').replace(/\s+/g, ' ').trim();
}

function advDisToneFor(segments: FtLogSegment[]): 'adv' | 'dis' | undefined {
    const selected = segments.find((seg) => isAdvDisDie(seg) && seg.isKept);
    const representative = selected || segments.find(isAdvDisDie);
    if (representative?.kind === 'adv-die') return 'adv';
    if (representative?.kind === 'dis-die') return 'dis';
    return undefined;
}

/**
 * A quick D20 normally leads with the D20 glyph. When that D20 was rolled as a pool, the owner's
 * carve-out is more informative: ADV/DIS replaces that generic glyph while skill, attack, and other
 * subject-specific icons remain intact. A plain single D20 has no pool and is therefore unchanged.
 */
function advDisD20EventIcon(segments: FtLogSegment[], renderedIcon?: string): string | undefined {
    const tone = advDisToneFor(segments);
    if (!tone) return renderedIcon;
    const normalized = String(renderedIcon || '').trim().toLowerCase();
    return normalized === 'd20' || normalized === 'dice-d20' ? tone : renderedIcon;
}

function elide(segments: FtLogSegment[]): FtLogSegment[] {
    let budget = SEGMENT_TEXT_CAP;
    const out: FtLogSegment[] = [];
    for (const seg of segments) {
        if (budget <= 0) break;
        if (seg.text.length <= budget) {
            out.push(seg);
            budget -= seg.text.length;
            continue;
        }
        out.push({ ...seg, text: `${seg.text.slice(0, Math.max(1, budget - 1)).trimEnd()}…` });
        budget = 0;
    }
    return out;
}

/**
 * The fold-out's full text, only when the collapsed line had to cut something. Otherwise it repeated
 * the row word for word above the time footer (owner 2026-09-10).
 */
function detailIfElided(fullText: string, kept: FtLogSegment[]): string | undefined {
    return fullText !== segmentsPlainText(kept) ? fullText : undefined;
}

/**
 * Belt-and-braces on top of the redaction the markup already carries. On a hidden roll:
 *   - every segment that could restate a value is dropped outright (`damage`, `result`, `effect`);
 *   - any bare number left in prose is masked, because a handler could word its result inline
 *     instead of badging it;
 *   - a `redacted` marker is appended so the line reads as deliberately withheld rather than as a
 *     sentence that trailed off.
 * The row markup should not contain any of this in the first place — this guarantees it even if a
 * future handler forgets one of the three per-part redaction sites.
 */
function redactSegments(segments: FtLogSegment[]): FtLogSegment[] {
    const kept = segments
        .filter((seg) => seg.kind === 'text' || seg.kind === 'ref')
        .map((seg) => (seg.kind === 'text'
            ? { ...seg, text: seg.text.replace(/\d+(?:[.,]\d+)?/g, '•') }
            : seg));
    if (kept.length === 0) return kept;
    kept.push({ kind: 'redacted', text: '(hidden)' });
    return kept;
}

/**
 * Strip the parts that are read separately, so they cannot also appear in the prose: the portrait
 * cell (it becomes entry.actor) and the timestamp footer (it becomes entry.time). Operates on a
 * DETACHED copy — the live path already owns a scratch node, and the seed path must never mutate
 * the legacy log it is reading from.
 */
function stripExtractedParts(root: Element): void {
    timestampSpans(root).forEach((span) => {
        // Remove the whole footer row, not just the reading, or its column scaffolding leaves gaps.
        (span.closest('.row') || span).remove();
    });
    root.querySelector('.col-3.border-end')?.remove();
    // The target is read as a part too; leaving it here would repeat the name as a bare tooltip.
    targetBlockIn(root)?.remove();
}

/**
 * The current renderer already emits compact damage outcomes. Normalize older stored markup too:
 * "10 Radiant damage" becomes the colour-coded "10", while the complete phrase remains available
 * through the Bootstrap tooltip. Scoped to the renderer's outcome marker so ordinary roll badges
 * are never mistaken for damage.
 */
function compactDamageOutcomes(root: Element): void {
    root.querySelectorAll<HTMLElement>('[data-ft-damage-outcome]').forEach((outcome) => {
        const badge = outcome.querySelector<HTMLElement>('.badge');
        if (!badge) return;
        const raw = (badge.textContent || '').replace(/\s+/g, ' ').trim();
        const match = /^([+-]?\d+(?:[.,]\d+)?)(?:\s+(.+))?$/.exec(raw);
        if (!match?.[1]) return;

        const amount = match[1];
        const damageType = (match[2] || '').trim();
        if (damageType) {
            const label = `${amount} ${damageType} damage`;
            if (!badge.getAttribute('data-bs-original-title') && !badge.getAttribute('title')) {
                badge.setAttribute('data-bs-toggle', 'tooltip');
                badge.setAttribute('data-bs-placement', 'top');
                badge.setAttribute('data-bs-original-title', label);
                badge.setAttribute('title', label);
            }
            badge.textContent = amount;
        }

        // Legacy markup placed the word after the badge; retain any following absorbed marker.
        let sibling = badge.nextSibling;
        while (sibling?.nodeType === Node.TEXT_NODE) {
            const text = sibling.textContent || '';
            sibling.textContent = text.replace(/^\s*damage\b\s*\.?\s*/i, '');
            if ((sibling.textContent || '') !== '') break;
            sibling = sibling.nextSibling;
        }
        const last = outcome.lastChild;
        if (last?.nodeType === Node.TEXT_NODE && /^[\s.]*$/.test(last.textContent || '')) last.remove();
    });
}

/**
 * Lift the ADV/DIS sources list out of a DETACHED row copy and remove it (and its icon toggle) so
 * the prose walk never sees it. The legacy renderer hides the list with display:none, so without
 * this the walker dropped it silently and the HUD could not say why a roll had ADV/DIS. The markup
 * only exists on rows the renderer did not redact — a hidden roll returns before the trace is built.
 */
function extractAdvDisTrace(root: Element): FtLogAdvDisTrace | undefined {
    const lists = Array.from(root.querySelectorAll<HTMLElement>('[data-ft-adv-dis-trace]'));
    root.querySelectorAll('[data-ft-adv-dis-trace-toggle]').forEach((el) => el.remove());
    if (lists.length === 0) return undefined;

    let net = 0;
    const sources: FtLogAdvDisSource[] = [];
    const attr = (el: Element, name: string): string | undefined => {
        const value = (el.getAttribute(`data-ft-adv-dis-source-${name}`) || '').trim();
        return value === '' ? undefined : value;
    };
    lists.forEach((list) => {
        net += parseInt(list.getAttribute('data-ft-adv-dis-trace-net') || '0', 10) || 0;
        list.querySelectorAll('[data-ft-adv-dis-source]').forEach((row) => {
            sources.push({
                delta: parseInt(attr(row, 'delta') || '0', 10) || 0,
                label: attr(row, 'label') || '',
                itemId: attr(row, 'item-id'),
                kind: attr(row, 'kind'),
                kindLabel: attr(row, 'kind-label'),
                kindIcon: attr(row, 'kind-icon'),
                originName: attr(row, 'origin-name'),
                originItemId: attr(row, 'origin-item-id'),
                originThumb: attr(row, 'origin-thumb'),
            });
        });
        list.remove();
    });
    if (sources.length === 0) return undefined;
    // A payload without adv_dis_stacks renders net 0; the sources' own sum still gives the right tone.
    if (net === 0) net = sources.reduce((sum, source) => sum + source.delta, 0);
    return { net, sources };
}

/** Keep the canonical damage outcome in the summary and its arithmetic in the fold-out. */
function extractDamageBreakdown(root: Element): string | undefined {
    if (!root.querySelector('[data-ft-damage-outcome]')) return undefined;
    const breakdown = root.querySelector('[data-ft-damage-breakdown]');
    if (!breakdown) return undefined;
    const parts: FtLogSegment[] = [];
    collectSegments(breakdown, parts);
    breakdown.remove();
    // The card's large total repeats the outcome too. Do not deduplicate by numeric equality:
    // equal dice and equal amounts of different damage types can be meaningful.
    root.querySelectorAll('h2, .h2').forEach(el => el.remove());
    const text = parts.map(part => part.tooltip
        ? `${segmentPlainText(part)} (${part.tooltip})`
        : segmentPlainText(part)).join(' ').replace(/\s+/g, ' ').trim();
    return text ? `Damage calculation: ${text}` : undefined;
}

function projectFromHtml(html: string): { segments: FtLogSegment[]; advDis?: FtLogAdvDisTrace; damageDetail?: string } {
    if (!html) return { segments: [] };
    const scratch = document.createElement('div');
    scratch.innerHTML = html;
    stripExtractedParts(scratch);
    const advDis = extractAdvDisTrace(scratch);
    compactDamageOutcomes(scratch);
    const damageDetail = extractDamageBreakdown(scratch);
    const collectedSegments: FtLogSegment[] = [];
    collectSegments(scratch, collectedSegments);
    return { segments: compactAdvDisPunctuation(collectedSegments), advDis, damageDetail };
}

function revealRollIdFrom(row: Element): string | undefined {
    return row.querySelector('[data-ft-log-action="make-roll-public"]')
        ?.getAttribute('data-ft-roll-id') || undefined;
}

/**
 * Drop the words an event glyph now stands for. Only the FIRST occurrence in prose is removed —
 * the phrase carries the meaning once; the icon carries it thereafter.
 */
function applyEventIcon(segments: FtLogSegment[], renderedIcon?: string): string | undefined {
    const prose = segments.filter((seg) => seg.kind === 'text').map((seg) => seg.text).join(' ');
    const hit = eventIconFor(prose);
    if (!hit) return renderedIcon;

    // ⚠ On some rows the phrase IS the whole message ("Combat Ended", a bare "Status Effect
    // Applied"), so removing it would leave a line with a glyph and nothing else. Only drop the
    // words when something is left to read; otherwise keep them and let the icon lead.
    const wouldEmpty = segments.every((seg) => {
        if (seg.kind !== 'text') return false;
        return seg.text.replace(hit.matched, '').trim() === '';
    });
    if (wouldEmpty) return renderedIcon || hit.icon;

    let replaced = false;
    for (const seg of segments) {
        if (seg.kind !== 'text' || replaced || !hit.matched.test(seg.text)) continue;
        seg.text = seg.text.replace(hit.matched, '').replace(/\s+/g, ' ').trim();
        replaced = true;
    }
    return renderedIcon || hit.icon;
}

/**
 * Project a row that is ALREADY in the legacy log (#div_sidelog_results > ul.list-group), so the HUD
 * log can open showing history instead of an empty box that only fills up as new rolls arrive. Those
 * rows are the same renderer output the live path walks, and they are already private-roll redacted
 * — which is exactly why this surface reads markup rather than payloads.
 *
 * The per-row privacy flags are not available here (there is no Roll payload attached to a rendered
 * row), so `redacted` is reported false and the markup stays the authority — the same contract the
 * live path relies on, minus the belt-and-braces pass.
 */
export function projectRenderedRow(row: Element, fallbackRollId = ''): FtLogEntry | null {
    // The row is LIVE in the legacy log, so read its parts before working on a detached copy —
    // stripExtractedParts() must never mutate what it is reading from.
    const actor = actorFrom(row);
    const target = targetFrom(row);
    const times = timesFrom(row);

    const scratch = row.cloneNode(true) as Element;
    stripExtractedParts(scratch);
    const advDis = extractAdvDisTrace(scratch);
    compactDamageOutcomes(scratch);
    const damageDetail = extractDamageBreakdown(scratch);
    const renderedEventIcon = renderedEventIconFor(scratch);
    const collectedSegments: FtLogSegment[] = [];
    collectSegments(scratch, collectedSegments);
    const segments = compactAdvDisPunctuation(collectedSegments);
    const eventIcon = advDisD20EventIcon(segments, applyEventIcon(segments, renderedEventIcon));
    const fullText = segmentsPlainText(segments);
    const kept = elide(segments);
    if (kept.length === 0 && !actor) return null;
    return {
        detailText: [detailIfElided(fullText, kept), damageDetail].filter(Boolean).join(' · ') || undefined,
        // Inline rows inside the legacy log's collapsible groups deliberately suppress their own
        // data-ft-roll-id: the outer rollup owns it. The history seeder hands that id back down so
        // the HUD can reconstruct the event instead of flattening its children into anonymous
        // prose after a reload.
        rollId: row.getAttribute('data-ft-roll-id') || fallbackRollId,
        rollI: null,
        revealRollId: revealRollIdFrom(row),
        kind: '',
        at: performance.now(),
        grouped: false,
        redacted: false,
        actor,
        target,
        campaignTime: times.campaign,
        realTime: times.real,
        eventIcon,
        advDis,
        segments: kept,
        fallbackText: segmentsPlainText(kept),
    };
}

function contestFromRenderedRow(row: Element): FtLogContest | undefined {
    const sideEls = Array.from(row.querySelectorAll<HTMLElement>('.col-5')).slice(0, 2);
    const leftEl = sideEls[0];
    const rightEl = sideEls[1];
    if (!leftEl || !rightEl) return undefined;

    const side = (el: HTMLElement) => {
        const resultText = (el.querySelector('.text-center.mt-1 .badge')?.textContent || '').trim();
        const parsed = Number(resultText);
        return {
            actor: actorFromCell(el),
            resultText: resultText || 'Hidden',
            resultValue: Number.isFinite(parsed) && resultText !== '' ? parsed : undefined,
            won: el.classList.contains('bg-success'),
        };
    };

    const left = side(leftEl);
    const right = side(rightEl);
    const differenceKnown = left.resultValue !== undefined && right.resultValue !== undefined;
    const difference = differenceKnown ? left.resultValue! - right.resultValue! : undefined;
    return { left, right, difference };
}

/**
 * The legacy log condenses a shared-roll_id event into one visible anchor plus a Bootstrap collapse.
 * Return those already-authorized rows as separate HUD entries, in anchor-first display order. The
 * caller remains responsible for ordering whole events (the legacy list itself is newest-first).
 *
 * `pairedAnchor` is used by death/trade containers, whose visible anchor and detail rollup are two
 * adjacent top-level <li>s. Generic and contest groups keep the anchor inside `row` itself.
 */
export function projectRenderedGroup(row: Element, pairedAnchor?: Element): FtLogEntry[] {
    const rollId = row.getAttribute('data-ft-roll-id') || '';
    const detail = row.querySelector<Element>([
        ':scope > div.mass-contest-detail',
        ':scope > div.ft-roll-group-detail',
        ':scope > ul.death-log-detail',
        ':scope > div.death-log-detail',
        ':scope > ul.trade-log-trade-detail',
        ':scope > div.trade-log-trade-detail',
    ].join(', '));

    if (!detail) {
        const single = projectRenderedRow(row, rollId);
        return single ? [single] : [];
    }

    const entries: FtLogEntry[] = [];
    const anchorSource = pairedAnchor || row;
    const anchor = anchorSource.cloneNode(true) as Element;

    // Remove the legacy collapse's UI and its child rows from the anchor copy. Without this, an
    // opened legacy collapse is flattened into the HUD summary and text such as "Show details (4)"
    // becomes part of the roll. Gate/rider collapses nested inside the actual row are intentionally
    // left alone; they describe that row rather than this shared-roll_id container.
    anchor.querySelectorAll([
        ':scope > .mass-contest-toggle-wrap',
        ':scope > .ft-roll-group-toggle-wrap',
        ':scope > .death-log-toggle-wrap',
        ':scope > .trade-log-trade-toggle-wrap',
        ':scope > div.mass-contest-detail',
        ':scope > div.ft-roll-group-detail',
        ':scope > ul.death-log-detail',
        ':scope > div.death-log-detail',
        ':scope > ul.trade-log-trade-detail',
        ':scope > div.trade-log-trade-detail',
    ].join(', ')).forEach((el) => el.remove());

    const anchorEntry = projectRenderedRow(anchor, rollId);
    if (anchorEntry) {
        const contest = row.classList.contains('mass-contest-rollup')
            ? contestFromRenderedRow(row)
            : undefined;
        if (contest) {
            anchorEntry.contest = contest;
            anchorEntry.kind = 'contesting_roll';
            // The generic prose walker sees the contest card's visual scaffolding as a string of
            // bare totals ("18 VS 7 11"). That is not useful information detail. The dedicated
            // contest summary + its expanded per-roll children are the readable authority.
            anchorEntry.detailText = undefined;
            anchorEntry.segments = [];
            const difference = contest.difference === undefined
                ? 'hidden'
                : `${contest.difference >= 0 ? '+' : ''}${contest.difference}`;
            anchorEntry.fallbackText = [
                contest.left.actor?.name || 'Someone',
                contest.left.resultText,
                'VS',
                contest.right.actor?.name || 'Someone',
                contest.right.resultText,
                `: ${difference}`,
            ].join(' ');
        }
        entries.push(anchorEntry);
    }

    Array.from(detail.children).forEach((child) => {
        const entry = projectRenderedRow(child, rollId);
        if (entry) entries.push(entry);
    });

    return entries;
}

export function projectAuthorizedRoll(detail: unknown): FtLogEntry | null {
    if (!detail || typeof detail !== 'object') return null;
    const d = detail as { roll?: unknown; html?: unknown; grouped?: unknown };
    const roll = (d.roll && typeof d.roll === 'object') ? d.roll as Record<string, unknown> : null;
    const visibility = roll ? rollVisibility(roll) : 'values';
    if (visibility === 'absent') return null;
    const html = typeof d.html === 'string' ? d.html : '';

    // Read the parts off the same markup before it is reduced to prose.
    const scratch = document.createElement('div');
    scratch.innerHTML = html;
    const actor = actorFrom(scratch);
    const target = targetFrom(scratch);
    const times = timesFrom(scratch);
    const renderedEventIcon = renderedEventIconFor(scratch);

    const { segments, advDis, damageDetail } = projectFromHtml(html);
    // A grouped or buildLog()-backed frame ships no row markup. Rather than invent a second
    // description of the event, stay quiet and let the log itself carry it.
    if (segments.length === 0 && !actor) return null;

    const redacted = visibility === 'hidden';
    // A hidden roll still names who rolled — that is what the legacy log shows too; only the values
    // are withheld. The event glyph goes with them: it would otherwise hint at what happened.
    const eventIcon = redacted
        ? undefined
        : advDisD20EventIcon(segments, applyEventIcon(segments, renderedEventIcon));
    const fullText = segmentsPlainText(redacted ? redactSegments(segments) : segments);
    const kept = elide(redacted ? redactSegments(segments) : segments);
    if (kept.length === 0 && !actor) return null;
    // Derived from what SURVIVED redaction, never from the raw projection — otherwise the
    // degradation path would carry text the entry itself refused to show.
    const fallbackText = segmentsPlainText(kept);

    const rollI = roll && roll.roll_i != null ? Number(roll.roll_i) : null;
    const rawResult = !redacted && roll && roll.roll_result != null ? Number(roll.roll_result) : NaN;
    return {
        detailText: [detailIfElided(fullText, kept), redacted ? undefined : damageDetail].filter(Boolean).join(' · ') || undefined,
        rollId: roll ? String(roll.roll_id || '') : '',
        rollI: Number.isFinite(rollI as number) ? rollI : null,
        revealRollId: revealRollIdFrom(scratch),
        kind: roll ? String(roll.roll_type_1 || '') : '',
        at: performance.now(),
        grouped: !!d.grouped,
        redacted,
        actor,
        target,
        campaignTime: times.campaign,
        realTime: times.real,
        eventIcon,
        resultValue: Number.isFinite(rawResult) ? rawResult : undefined,
        // Belt-and-braces: a hidden roll's markup never carries a trace, but never forward one.
        advDis: redacted ? undefined : advDis,
        segments: kept,
        fallbackText,
    };
}
